<script lang="ts" setup>
import axios from 'axios'
import { computed, onBeforeMount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useDark, useIntervalFn } from '@vueuse/core'

import { localAsset, image as imageHelper, image } from '@/utils/assets'
import { Marker, ResourceResponse } from '@/types/general'
import { datetime, localeDate } from '@/utils/dates'
import { ShipmentType, ShipmentTracking } from '@/types/delivery-management'
import { LocationPoint } from '@/types/company'
import { markerColors } from '@/utils/maps'

import MyImageViewer from '@/components/my-components/MyImageViewer.vue'
import MyFileAttachment from '@/components/my-components/MyFileAttachment.vue'
import MyInputLabel from '@/components/my-components/form/MyInputLabel.vue'
import MyPanel from '@/components/my-components/MyPanel.vue'
import MapComponent from '@/components/MapComponent.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'

const darkMode = useDark()
const route = useRoute()
const { t } = useI18n()

const shipment = ref<ShipmentTracking | null>(null)
const loading = ref(false)

const shipmentIsPickup = computed(() => shipment.value?.type === ShipmentType.Pickup)
const shipmentNumber = computed(() => route.params.shipmentNumber)
const companySlug = computed(() => route.params.slug)

const mypalletLogo = computed(() => {
    if (darkMode.value) {
        return 'https://imagedelivery.net/Dw1gWFsgRYaCpgB4c6PD1Q/94800510-1c2c-43d1-9663-03353d2a7000/public'
    }

    return 'https://imagedelivery.net/Dw1gWFsgRYaCpgB4c6PD1Q/51a8d0e1-f0cc-4a8f-b164-2bbfbd825600/public'
})

const estimatedAtTitle = computed(() => {
    if (!shipment.value) return ''
    if (shipment.value.unsuccessful && shipment.value.type === ShipmentType.Delivery) {
        return t('unsuccessfulDeliveryAt')
    }
    if (shipment.value.unsuccessful && shipment.value.type !== ShipmentType.Delivery) {
        return t('unsuccessfulPickupAt')
    }
    if (shipment.value.handledAt) return t('handledAt')
    if (shipment.value.type === ShipmentType.Delivery) return t('estimatedDeliveryAt')

    return t('estimatedPickupAt')
})

const lastHandledShipmentIndex = computed(() => {
    if (!shipment.value?.shipmentsHandled) return -1
    return shipment.value.shipmentsHandled.lastIndexOf(true) + 1
})

const shipmentStatusText = computed(() => {
    if (!shipment.value?.shipmentsHandled) return ''
    if (!shipment.value.tour?.startedAt) return ''
    if (shipment.value.handledAt) return t('delivered')
    const totalShipments = shipment.value.shipmentsHandled.length
    const handledShipments = lastHandledShipmentIndex.value
    const shipmentsLeft = totalShipments - handledShipments

    if (shipmentsLeft <= 0) return t('outForDelivery')
    if (shipmentsLeft === 1) return t('oneStopBeforeDelivery')
    return t('stopsBeforeDelivery', { count: shipmentsLeft })
})

const showShipmentDetails = computed(() => {
    if (!shipment.value) return false
    if (!shipment.value.handledAt) return false
    if (shipment.value.receiverName) return true
    if (shipment.value.receiverSignature) return true
    if (shipment.value.images.length > 0) return true

    return false
})

const markers = computed<Marker[]>(() => {
    const markerList: Marker[] = []

    if (!shipment.value || !shipment.value.destinationLocation) return markerList

    if (isValidLocation(shipment.value.destinationLocation)) {
        markerList.push({
            location: shipment.value.destinationLocation,
            content: t('destination'),
            fillColor: markerColors.green,
        })
    }

    if (
        !shipment.value.handledAt &&
        shipment.value.driverLatestLocation &&
        isValidLocation(shipment.value.driverLatestLocation)
    ) {
        // We'll flip the marker so it always looks like the truck is driving towards the destination location
        const style =
            shipment.value.driverLatestLocation.longitude >
            shipment.value.destinationLocation.longitude
                ? 'transform: scale(-1,1)'
                : ''

        const truckSvg = `<svg xmlns="http://www.w3.org/2000/svg" style="${style}" fill="#16a085" viewBox="0 0 24 24"><path d="M18 18.5a1.5 1.5 0 0 1-1.5-1.5 1.5 1.5 0 0 1 1.5-1.5 1.5 1.5 0 0 1 1.5 1.5 1.5 1.5 0 0 1-1.5 1.5m1.5-9 1.96 2.5H17V9.5m-11 9A1.5 1.5 0 0 1 4.5 17 1.5 1.5 0 0 1 6 15.5 1.5 1.5 0 0 1 7.5 17 1.5 1.5 0 0 1 6 18.5M20 8h-3V4H3c-1.11 0-2 .89-2 2v11h2a3 3 0 0 0 3 3 3 3 0 0 0 3-3h6a3 3 0 0 0 3 3 3 3 0 0 0 3-3h2v-5l-3-4Z"/></svg>`

        markerList.push({
            markerId: 'driver-location',
            location: shipment.value.driverLatestLocation,
            content: t('driverLocation'),
            customSvg: truckSvg,
        })
    }

    return markerList
})

const products = computed(() => {
    if (!shipment.value?.transactions) return []

    return shipment.value?.transactions.flatMap((transaction) => transaction.products) ?? []
})

async function fetchShipment() {
    loading.value = true
    try {
        const response = await axios.get<ResourceResponse<ShipmentTracking>>(
            window.route('tracking.shipments.show', {
                company: companySlug.value,
                shipmentNumber: shipmentNumber.value,
            }),
        )
        shipment.value = response.data.data
    } finally {
        loading.value = false
    }
}

function isValidLocation(location: LocationPoint | null): location is LocationPoint {
    if (!location) return false
    return location.latitude !== 0 && location.longitude !== 0
}

onBeforeMount(async () => {
    await fetchShipment()
})

// Refresh shipment data every minute
useIntervalFn(fetchShipment, 1_000 * 60)
</script>

<template>
    <img
        v-if="shipment?.tour?.companyLogo"
        class="mt-2 w-32 self-center mb-12 rounded-xl bg-primary-200"
        :src="imageHelper(shipment?.tour?.companyLogo, 'thumbnail')"
    />

    <MyPanel
        v-if="shipment"
        panel-class="flex flex-col md:w-1/3 sm:w-full mx-auto justify-center shadow-lg mx-4 mb-4"
    >
        <LoaderWrapper :visible="loading" />
        <MapComponent
            v-if="shipment"
            class="h-[300px] rounded-t-lg overflow-hidden"
            :markers="markers"
            hide-map-options
        />

        <div
            v-if="!shipment.handledAt && shipment.tour?.startedAt"
            class="relative w-full flex bg-stone-400 h-2"
        >
            <div
                class="absolute inset-0 h-5.5 backdrop-blur-lg bg-white/20 border-t border-white/30 -mt-5.5 z-[5]"
            />
            <div
                v-for="(handled, index) in shipment.shipmentsHandled"
                :key="index"
                class="w-full h-full border-r border-stone-500 last-of-type:border-none"
                :class="{
                    'bg-green-500': handled,
                    'bg-stone-400': !handled,
                }"
            >
                <mdi:truck-delivery
                    v-if="index === lastHandledShipmentIndex"
                    class="absolute -top-5 w-5 h-5 text-green-500 z-10 -ml-2.5"
                />
                <mdi:map-marker
                    v-if="index === shipment.shipmentsHandled.length - 1"
                    class="absolute -top-5 right-0 w-4.5 h-4.5 text-green-500 z-10"
                />
            </div>
        </div>

        <div class="flex my-4 flex-col items-center">
            <h1 class="text-lg font-semibold text-primary-400" v-text="t('shipmentDetails')" />
            <span
                class="font-semibold text-xs text-primary-400 opacity-60"
                v-text="shipmentStatusText"
            />
        </div>

        <div v-if="shipment.unsuccessful" class="flex space-x-1 items-center">
            <span class="font-semibold text-primary-400">|</span>
            <h1 class="text-lg font-semibold text-red-400" v-text="t('unsuccessful')" />
        </div>

        <div class="flex justify-between space-x-3 px-6 pb-6">
            <div class="flex w-1/2 flex-col space-y-3">
                <div class="flex flex-col">
                    <span
                        class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                        v-text="t('shipmentNumber')"
                    />
                    <span
                        class="text-md font-semibold text-primary-500 dark:text-primary-100"
                        v-text="shipment.shipmentNumber ?? t('notSpecified')"
                    />
                </div>

                <div class="flex justify-between flex-col space-x-2">
                    <span
                        class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                        v-text="t('sender')"
                    />
                    <span
                        class="text-md font-semibold text-primary-500 dark:text-primary-100"
                        v-text="shipment.sendingCompanyName"
                    />
                </div>

                <div v-if="shipment.shipperCompany" class="flex flex-col justify-between space-x-2">
                    <span
                        class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                        v-text="t('shipper')"
                    />
                    <span
                        class="text-md font-semibold text-primary-500 dark:text-primary-100"
                        v-text="shipment.shipperCompanyName"
                    />
                </div>

                <div class="flex justify-between flex-col space-x-2">
                    <span
                        class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                        v-text="t('deliveryAddress')"
                    />
                    <span
                        class="text-md font-semibold text-primary-500 dark:text-primary-100"
                        v-text="shipment.address"
                    />
                </div>
            </div>

            <!-- Timeline -->
            <div class="flex w-1/2 space-x-2">
                <div class="mt-2">
                    <div class="h-5 w-5 flex-shrink-0 rounded-full bg-primary-400" />
                    <div class="my-2 h-4 w-0.5 mx-auto bg-gray-500" />

                    <!-- ShipmentLoaded -->
                    <div
                        v-if="shipment.shipmentLoadedAt"
                        class="h-5 w-5 flex-shrink-0 rounded-full bg-primary-400"
                    />
                    <div v-else class="h-5 w-5 flex-shrink-0 rounded-full bg-gray-400" />
                    <div class="my-2 h-4 w-0.5 mx-auto bg-gray-500" />

                    <!-- HandledAt -->
                    <div
                        v-if="shipment.unsuccessful"
                        class="h-5 w-5 flex-shrink-0 border-2 border-red-400 rounded-full bg-red-400"
                    >
                        <mdi:close class="h-4 w-4 text-white" />
                    </div>
                    <div
                        v-else-if="shipment.handledAt"
                        class="h-5 w-5 flex-shrink-0 rounded-full bg-green-400"
                    >
                        <mdi:check class="h-4 w-4 ml-0.5 pt-1 text-white" />
                    </div>
                    <div v-else class="h-5 w-5 flex-shrink-0 rounded-full bg-gray-400" />
                </div>

                <div class="flex space-x-2" />

                <!-- Datetimes -->
                <div class="flex flex-col space-y-3">
                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="t('plannedAt')"
                        />
                        <span
                            class="text-md font-semibold text-primary-500 dark:text-primary-100"
                            v-text="localeDate(shipment.plannedAt)"
                        />
                    </div>
                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="
                                shipmentIsPickup ? t('unloadedAfterPickup') : t('loadedOnVehicle')
                            "
                        />
                        <span
                            v-if="shipment.shipmentLoadedAt"
                            class="text-md font-semibold text-primary-500 dark:text-primary-100"
                            v-text="datetime(shipment.shipmentLoadedAt)"
                        />
                        <span
                            v-else
                            class="text-md font-semibold text-primary-500 dark:text-primary-100"
                            v-text="t('awaiting')"
                        />
                    </div>
                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="estimatedAtTitle"
                        />
                        <span
                            v-if="shipment.handledAt"
                            class="text-md font-semibold text-primary-500 dark:text-primary-100"
                            v-text="datetime(shipment.handledAt)"
                        />
                        <span
                            v-else-if="shipment.tourEventEstimatedAt"
                            class="text-md font-semibold text-primary-500 dark:text-primary-100"
                            v-text="datetime(shipment.tourEventEstimatedAt)"
                        />

                        <span
                            v-else
                            class="text-md font-semibold text-primary-500 dark:text-primary-100"
                            v-text="t('notSpecified')"
                        />
                    </div>
                </div>
            </div>
        </div>
        <!-- Collis -->
        <div v-if="shipment.collis && shipment.collis.length > 0" class="px-6 pb-6">
            <span
                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                v-text="t('collis')"
            />

            <div
                class="grid w-full gap-3 mt-1"
                :class="
                    shipment.collis.length === 1 ? 'grid-cols-1' : 'md:grid-cols-2 sm:grid-cols-1'
                "
            >
                <div
                    v-for="colli in shipment.collis"
                    :key="colli.id"
                    class="flex dark:bg-dark-400 bg-gray-200 rounded-xl justify-between p-3"
                >
                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="t('width')"
                        />
                        <span
                            class="text-xs font-semibold text-primary-500 dark:text-primary-100"
                            v-text="colli.width + ' ' + t('cm')"
                        />
                    </div>

                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="t('length')"
                        />
                        <span
                            class="text-xs font-semibold text-primary-500 dark:text-primary-100"
                            v-text="colli.length + ' ' + t('cm')"
                        />
                    </div>
                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="t('height')"
                        />
                        <span
                            class="text-xs font-semibold text-primary-500 dark:text-primary-100"
                            v-text="colli.height + ' ' + t('cm')"
                        />
                    </div>
                    <div class="flex flex-col">
                        <span
                            class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                            v-text="t('weight')"
                        />
                        <span
                            class="text-xs font-semibold text-primary-500 dark:text-primary-100"
                            v-text="colli.weight + ' ' + t('kg')"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- Delivery Information -->
        <div v-if="showShipmentDetails" class="flex flex-col space-y-1 px-6 pb-6">
            <span
                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                v-text="t('deliveryDetails')"
            />

            <div class="flex w-full dark:bg-dark-400 bg-gray-200 rounded-xl p-3 space-x-6">
                <div class="flex flex-col w-1/2">
                    <span
                        class="text-md font-semibold text-primary-500 dark:text-primary-100"
                        v-text="shipment.receiverName"
                    />
                    <img
                        v-if="shipment.receiverSignature"
                        class="w-32 m-1"
                        :src="image(shipment.receiverSignature)"
                    />
                </div>

                <MyImageViewer
                    v-if="shipment.images.length > 0"
                    class="grid grid-cols-2 gap-3 w-1/2 [direction:rtl]"
                >
                    <MyFileAttachment
                        v-for="file in shipment.images"
                        :key="file.id"
                        class="cursor-pointer !min-h-[5rem] !max-h-[5rem] [direction:rtl]"
                        :file="file"
                    />
                </MyImageViewer>
            </div>
        </div>

        <!-- Packaging Details -->
        <div
            v-if="shipment.handledAt && products.length > 0"
            class="flex flex-col space-y-1 px-6 pb-6"
        >
            <span
                class="text-xs font-semibold text-primary-400 dark:text-primary-300"
                v-text="t('packagingDetails')"
            />
            <MyPanel bordered panel-class="overflow-hidden">
                <table class="w-full">
                    <thead>
                        <tr
                            class="border-b border-primary-200 bg-primary-100 text-xs dark:border-dark-400 dark:bg-dark-600"
                        >
                            <th class="p-2.5 font-semibold" colspan="2" v-text="t('name')" />
                            <th
                                class="w-20 border-x border-primary-200 p-2.5 font-semibold dark:border-dark-400"
                                v-text="t('delivered')"
                            />
                            <th class="w-20 p-2.5 font-semibold" v-text="t('received')" />
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="product in products"
                            :key="product.id"
                            class="border-t border-primary-200 first:border-t-0 even:bg-primary-100 dark:border-dark-400 dark:even:bg-dark-600"
                        >
                            <td class="w-[60px]">
                                <img
                                    :src="imageHelper(product.image, 'thumbnail')"
                                    :alt="product.name"
                                />
                            </td>
                            <td
                                class="border-r border-primary-200 p-2.5 dark:border-dark-400"
                                v-text="product.name"
                            />
                            <td
                                class="border-r border-primary-200 text-center dark:border-dark-400"
                            >
                                {{ product.delivered }}
                                <span
                                    v-if="product.deliveredFilled"
                                    class="mb-0.5 ml-2 inline-block h-2 w-2 rounded-full bg-blue-500"
                                />
                            </td>
                            <td class="text-center">
                                {{ product.received }}
                                <span
                                    v-if="product.receivedFilled"
                                    class="mb-0.5 ml-2 inline-block h-2 w-2 rounded-full bg-blue-500"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </MyPanel>
        </div>

        <img
            :src="localAsset('assets/begreenprintlabel.png')"
            alt="begreen"
            class="absolute bottom-0 right-0 w-32 h-32 hidden opacity-30 print:block"
        />
    </MyPanel>
    <h1
        v-if="!shipment && !loading"
        class="my-auto text-center text-4xl font-bold"
        v-text="t('shipmentNotFound')"
    />

    <div class="flex flex-col justify-center space-y-2">
        <MyInputLabel class="mt-4 text-center" v-text="t('poweredBy')" />
        <img alt="myPallet Logo" class="w-36 h-auto mx-auto" :src="mypalletLogo" />
    </div>
</template>
